import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import AuthLayout from "./AuthLayout";
import { Button } from "../components/kit";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail, TIMEZONE } from "../lib/api";

export default function Register() {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState("owner");
  const [referred, setReferred] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const code = searchParams.get("code");
    if (searchParams.get("role") === "coach") setRole("coach");
    if (code) {
      setRole("owner");
      setInviteCode(code.toUpperCase());
      setReferred(true);
    }
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const asCoach = role === "coach";
      const user = await register({
        name, email, password, timezone: TIMEZONE,
        as_coach: asCoach,
        referred_by_coach: !asCoach && referred,
        invite_code: !asCoach ? inviteCode.trim() : "",
      });
      navigate(user.role === "coach" ? "/coach" : "/onboarding");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Get started" subtitle="Create your account, then build your baseline in a few minutes." testId="register-page">
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {error && <div data-testid="register-error" style={errStyle}>{error}</div>}

        <div style={{ display: "flex", gap: "var(--space-2)" }} data-testid="register-role-toggle">
          {[["owner", "I run a business"], ["coach", "I'm a coach"]].map(([val, lbltxt]) => (
            <button key={val} type="button" data-testid={`register-role-${val}`} onClick={() => setRole(val)}
              style={{
                flex: 1, padding: "10px 12px", borderRadius: "var(--radius-md)", cursor: "pointer",
                font: "var(--type-label)", fontWeight: 600,
                border: "1.5px solid " + (role === val ? "var(--buktu-green)" : "var(--border-hairline)"),
                background: role === val ? "var(--surface-brand-wash)" : "var(--surface-card)",
                color: role === val ? "var(--buktu-green-deep)" : "var(--text-muted)",
              }}>
              {lbltxt}
            </button>
          ))}
        </div>

        <label style={lbl}>Your name
          <input data-testid="register-name" className="buktu-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Octavia Butler" />
        </label>
        <label style={lbl}>Email
          <input data-testid="register-email" className="buktu-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.com" required />
        </label>
        <label style={lbl}>Password
          <div style={{ position: "relative" }}>
            <input data-testid="register-password" className="buktu-input" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" minLength={6} required style={{ paddingRight: 44, width: "100%" }} />
            <button type="button" data-testid="register-toggle-password" onClick={() => setShowPassword((s) => !s)} aria-label={showPassword ? "Hide password" : "Show password"} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: 0, cursor: "pointer", color: "var(--text-faint)", display: "grid", placeItems: "center", padding: 0 }}>
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>

        {role === "owner" && (
          <>
            <label style={lbl}>Coach invite code <span style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)", fontWeight: 400 }}>(optional)</span>
              <input data-testid="register-invite-code" className="buktu-input" value={inviteCode} onChange={(e) => { setInviteCode(e.target.value); if (e.target.value) setReferred(true); }} placeholder="e.g. BKSCALE" style={{ textTransform: "uppercase" }} />
            </label>
            <label data-testid="register-referred" onClick={() => setReferred((r) => !r)} style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", cursor: "pointer", padding: "var(--space-3) var(--space-4)", borderRadius: "var(--radius-md)", border: "1.5px solid var(--border-hairline)", background: referred ? "var(--surface-brand-wash)" : "var(--surface-card)" }}>
              <span style={{ width: 20, height: 20, borderRadius: 6, border: "2px solid " + (referred ? "var(--buktu-green)" : "var(--border-strong)"), background: referred ? "var(--buktu-green)" : "transparent", display: "grid", placeItems: "center", color: "#fff", fontSize: 12 }}>{referred ? "✓" : ""}</span>
              <span style={{ font: "var(--type-body-sm)", color: "var(--text-body)" }}>A coach or program referred me here</span>
            </label>
          </>
        )}
        {role === "coach" && (
          <p style={{ font: "var(--type-body-sm)", fontSize: 13, color: "var(--text-muted)", padding: "var(--space-3) var(--space-4)", borderRadius: "var(--radius-md)", background: "var(--surface-brand-wash)" }}>
            You'll get a unique invite code to share with your clients. As they build baselines, they appear in your cohort dashboard.
          </p>
        )}

        <Button type="submit" full size="lg" data-testid="register-submit" disabled={loading}>
          {loading ? "Creating account…" : "Create account"}
        </Button>
        <p style={{ font: "var(--type-body-sm)", color: "var(--text-muted)", textAlign: "center", marginTop: "var(--space-2)" }}>
          Already have an account? <Link to="/login" data-testid="register-login-link" style={{ color: "var(--text-link)" }}>Sign in</Link>
        </p>
      </form>
    </AuthLayout>
  );
}

const lbl = { display: "flex", flexDirection: "column", gap: "var(--space-2)", font: "var(--type-label)", color: "var(--text-strong)" };
const errStyle = { padding: "var(--space-3) var(--space-4)", borderRadius: "var(--radius-md)", background: "var(--status-risk-soft)", color: "#8a2a1a", font: "var(--type-body-sm)", fontSize: 13 };
